class AxMetaobjectProducts extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('product-list');
    this.forms = Array.from(this.querySelectorAll('[data-ax-filter-form]'));
    this.form = this.forms[0];
    this.emptyState = this.querySelector('[data-ax-empty]');
    this.pagination = this.querySelector('[data-ax-pagination]');
    this.perPage = parseInt(this.dataset.perPage, 10) || 0;
    this.page = 1;

    if (!this.list) {
      return;
    }

    this.items = Array.from(this.list.querySelectorAll('.ax-facet-item')).map((element, index) => ({
      element,
      index,
      price: parseInt(element.dataset.price, 10) || 0,
      available: element.dataset.available === 'true',
      title: element.dataset.title || '',
      facets: JSON.parse(element.dataset.axFacets || '{}')
    }));

    this.forms.forEach((form) => {
      form.addEventListener('submit', (event) => event.preventDefault());

      const onChange = (event) => {
        if (event.target.name === 'sort') {
          this.sort = event.target.value;
          this._syncSortLabel();
        }

        this._syncForms(form);
        this.page = 1;
        this._render();
      };

      form.addEventListener('change', onChange);
      form.addEventListener('input', onChange);

      form.querySelectorAll('[data-reset]').forEach((button) => {
        button.addEventListener('click', () => {
          button.closest('facet-dialog').querySelectorAll('input').forEach((input) => {
            input.checked = false;
          });

          this._syncForms(form);
          this.page = 1;
          this._render();
        });
      });
    });

    this.addEventListener('listbox:change', (event) => {
      this.sort = event.detail.value;

      this.forms.forEach((form) => {
        const radio = form.querySelector(`input[name="sort"][value="${this.sort}"]`);

        if (radio) {
          radio.checked = true;
        }
      });

      this.page = 1;
      this._render();
    });

    if (this.pagination) {
      this.pagination.querySelector('[data-ax-prev]').addEventListener('click', () => this._goToPage(this.page - 1));
      this.pagination.querySelector('[data-ax-next]').addEventListener('click', () => this._goToPage(this.page + 1));
    }

    this._render();
  }

  _syncForms(source) {
    this.forms.filter((form) => form !== source).forEach((target) => {
      source.querySelectorAll('input').forEach((input) => {
        const selector = input.type === 'checkbox' || input.type === 'radio'
          ? `input[name="${input.name}"][value="${CSS.escape(input.value)}"]`
          : `input[name="${input.name}"]`;
        const twin = target.querySelector(selector);

        if (!twin) {
          return;
        }

        if (input.type === 'checkbox' || input.type === 'radio') {
          twin.checked = input.checked;
        } else {
          twin.value = input.value;
        }
      });
    });
  }

  _syncSortLabel() {
    const radio = this.querySelector(`input[name="sort"][value="${this.sort}"]`);
    const label = this.querySelector('.collection-filter__sort-value');

    if (radio && label) {
      label.textContent = radio.closest('.checkbox-container').querySelector('label').textContent.trim();
    }

    this.querySelectorAll('.popover-listbox__option').forEach((option) => {
      option.setAttribute('aria-selected', option.value === this.sort);
    });
  }

  get _criteria() {
    const data = new FormData(this.form || undefined);
    const groups = {};

    data.forEach((value, key) => {
      if (!key.startsWith('facet:')) {
        return;
      }

      const id = key.slice(6);
      (groups[id] = groups[id] || []).push(value);
    });

    return {
      groups,
      availableOnly: data.get('available') === '1',
      priceMin: parseFloat(data.get('filter.v.price.gte')),
      priceMax: parseFloat(data.get('filter.v.price.lte'))
    };
  }

  _matches(item, criteria) {
    if (criteria.availableOnly && !item.available) {
      return false;
    }

    if (!isNaN(criteria.priceMin) && item.price < criteria.priceMin * 100) {
      return false;
    }

    if (!isNaN(criteria.priceMax) && item.price > criteria.priceMax * 100) {
      return false;
    }

    return Object.entries(criteria.groups).every(([id, selected]) => {
      const values = item.facets[id] || [];
      return selected.some((value) => values.includes(value));
    });
  }

  _sorted(items) {
    const comparators = {
      'price-ascending': (a, b) => a.price - b.price,
      'price-descending': (a, b) => b.price - a.price,
      'title-ascending': (a, b) => a.title.localeCompare(b.title),
      'title-descending': (a, b) => b.title.localeCompare(a.title)
    };

    return items.slice().sort(comparators[this.sort] || ((a, b) => b.available - a.available || a.index - b.index));
  }

  _goToPage(page) {
    this.page = page;
    this._render();
    this.list.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  _paginate(items) {
    if (!this.perPage) {
      return items;
    }

    const pages = Math.max(1, Math.ceil(items.length / this.perPage));
    this.page = Math.min(Math.max(1, this.page), pages);

    if (this.pagination) {
      const previous = this.pagination.querySelector('[data-ax-prev]');
      const next = this.pagination.querySelector('[data-ax-next]');

      this.pagination.hidden = pages < 2;
      this.pagination.querySelector('[data-ax-page]').textContent = `${this.page}\u00a0\u00a0/\u00a0\u00a0${pages}`;

      previous.disabled = this.page === 1;
      next.disabled = this.page === pages;
      previous.classList.toggle('pagination__item--disabled', previous.disabled);
      next.classList.toggle('pagination__item--disabled', next.disabled);
    }

    const start = (this.page - 1) * this.perPage;
    return items.slice(start, start + this.perPage);
  }

  _render() {
    const criteria = this._criteria;
    const matching = this._sorted(this.items.filter((item) => this._matches(item, criteria)));
    const visible = this._paginate(matching);

    this.items.forEach((item) => {
      item.element.hidden = true;
    });

    this.list.append(...visible.map((item) => {
      item.element.hidden = false;
      return item.element;
    }));

    if (this.emptyState) {
      this.emptyState.hidden = matching.length > 0;
    }

    this.querySelectorAll('facet-dialog').forEach((dialog) => {
      const counter = dialog.querySelector('[data-count]');

      if (counter) {
        const count = dialog.querySelectorAll('input:checked').length;
        counter.textContent = counter.textContent.replace(/^\d+/, count);
      }
    });
  }
}

if (!window.customElements.get('ax-metaobject-products')) {
  window.customElements.define('ax-metaobject-products', AxMetaobjectProducts);
}
